import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslocoModule } from '@jsverse/transloco';
import { Provider } from '@core/data/providers/base-provider.model';
import { CypressDirectiveModule } from '@shared/directives/cypress/cypress-directive.module';
import { ButtonModule } from '@shared/components/atoms/button/button.module';
import { TooltipDirectiveModule } from '@shared/directives/tooltip/tooltip-directive.module';
import { AddProviderComponent } from '../../privacy-cockpit/add-provider/add-provider.component';
import { Observable, take } from 'rxjs';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { QuestionnaireDialogService } from '@features/questionnaire-renderer/questionnaire-dialog.service';
import { AddQuestionnairesService } from '@core/data/questionnaires/add-questionnaire/add-questionnaire.service';
import { ConsentFacilitiesService } from '@core/services/consent/consent-facilities.service';
import { ConsentPractitionerProvidersService } from '@core/services/consent/consent-practitioner-providers.service';
import { QuestionnaireShareData } from '@features/questionnaire-renderer/models/questionnaire-selection-data.model';
import { UserMandantFacilitiesService } from '@core/services/user-mandant-facilities.service';
import { StepperDialogComponent } from '@shared/components/dialogs/stepper-dialog/stepper-dialog.component';

@Component({
  selector: 'xt-questionnaire-selection-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressBarModule,
    TranslocoModule,
    CypressDirectiveModule,
    ButtonModule,
    TooltipDirectiveModule,
    MatIcon,
    StepperDialogComponent,
  ],
  templateUrl: './questionnaire-share-dialog.component.html',
  styleUrls: ['./questionnaire-share-dialog.component.scss'],
})
export class QuestionnaireShareDialogComponent implements OnInit {
  protected readonly data = inject<QuestionnaireShareData>(MAT_DIALOG_DATA);
  private readonly dialog = inject(MatDialog);
  private readonly dialogRef = inject(
    MatDialogRef<QuestionnaireShareDialogComponent>,
  );
  private readonly addQuestionnairesService = inject(AddQuestionnairesService);
  private readonly questionnaireDialogService = inject(
    QuestionnaireDialogService,
  );
  private readonly consentFacilitiesService = inject(ConsentFacilitiesService);
  private readonly practitionerProviderSharedService = inject(
    ConsentPractitionerProvidersService,
  );
  private readonly userMandantFacilitiesService = inject(
    UserMandantFacilitiesService,
  );

  dialogConfig = { title: 'app.documents.questionnaires.share' };

  providersToShareWith: Provider[] = [];
  questionnaireList = [];

  isShareActionEnabled$: Observable<boolean> =
    this.userMandantFacilitiesService.hasActiveFacilities$;
  shareActionTooltip$: Observable<string> =
    this.userMandantFacilitiesService.tooltipDisabledSharing$;

  protected readonly currentStep = this.questionnaireDialogService.currentStep;
  protected readonly stepCount = this.questionnaireDialogService.stepCount;

  ngOnInit(): void {
    this.setQuestionnaireList();
    this.setApprovalList();
  }

  addProvider() {
    this.isShareActionEnabled$.subscribe((isShareActionEnabled) => {
      if (isShareActionEnabled) {
        const addProviderDialog = this.dialog.open(AddProviderComponent, {
          width: '880px',
          height: '90vh',
          data: {
            addDialog: 'addDialog',
          },
          panelClass: 'full-width',
        });

        addProviderDialog.afterClosed().subscribe((result) => {
          if (result) {
            this.providersToShareWith.push(result);
          }
        });
      }
    });
  }

  setQuestionnaireList() {
    this.addQuestionnairesService
      .getItemById$(this.data.questionnaireId)
      .subscribe((res) => {
        this.questionnaireList.push({ name: res.name });
      });
  }

  /**
   * remove element from providers list
   * @param provider provider to share with
   */
  removeProvider(provider: Provider) {
    this.providersToShareWith = this.providersToShareWith.filter(
      (i) => i !== provider,
    );
  }

  setApprovalList() {
    this.consentFacilitiesService
      .fetchFacilitiesWithConsentData()
      .pipe(take(1))
      .subscribe((data) => this.addProvidersToShareWith(data));

    this.practitionerProviderSharedService
      .fetchPractitionerProvidersWithConsentData()
      .pipe(take(1))
      .subscribe((data) => this.addProvidersToShareWith(data));
  }

  continueWithSharing() {
    this.isShareActionEnabled$.subscribe((isShareActionEnabled) => {
      if (isShareActionEnabled) {
        this.questionnaireDialogService.increaseCurrentStep();
        this.dialogRef.close();
        this.questionnaireDialogService.openShareConfirmationDialog({
          serviceRequestId: this.data.serviceRequestId,
          providersToShareWith: this.providersToShareWith,
        });
      }
    });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  private addProvidersToShareWith(providers: Provider[]) {
    for (const element of providers) {
      this.providersToShareWith.push(element);
      this.providersToShareWith = [...new Set(this.providersToShareWith)];
    }
  }
}
